import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CloseIcon from '@mui/icons-material/CloseRounded';

import { convertBlobToBase64, slideUp } from '../utils';
import styles from './SelfieEditCard.module.css';
import pstyles from './AskPermission.module.css';
import Compressor from 'compressorjs';
import SearchImageCard from './SearchImageCard'
import AIImageGenerationCard from './AIImageGenerationCard'
import { POSTTrackDownload, generateImageAPICall, getBase64FromUrl } from '../../env/APImanager'
import close from '../../assets/close.png'


function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height
  };
}
function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowDimensions;
}
const SelfieEditCard = ({ handleClose, initialValue, onValueChange, existingMessageText }) => {
  const [selfie, setSelfie] = useState(initialValue);
  const [aiImageUrl, setAiImageUrl] = useState(null);
  const [aiResponseId, setAiResponseId] = useState(null);
  const { height } = useWindowDimensions();

  let selfieInputElement = useRef(null);

  // Get token and other params from URL
  const search = window.location.search;
  const params = new URLSearchParams(search);
  const [tokenId, setTokenId] = useState(() => params.get("userTokenId"));
  // Use editTokenId as message_id (as per consumer app param name)
  const messageId = params.get("editTokenId");
  const inApp = params.get("inapp") === "true";
  const isAuthenticated =
    tokenId &&
    tokenId.trim() !== "" &&
    tokenId !== "null" &&
    tokenId !== "undefined";

  const handleChange = (e) => {
    setdownload_location("")
    const fileList = e.target.files;
    let file = null;


    for (let i = 0; i < fileList.length; i++) {
      if (fileList[i].type.match(/^image\//)) {
        file = fileList[i];
        break;
      }
    }

    if (file !== null) {
      // Clear AI metadata when user selects a non-AI image
      setAiImageUrl(null);
      setAiResponseId(null);
      new Compressor(file, {
        quality: 0.6,
        success(result) {
          convertBlobToBase64(result).then((selfieBase64) => {
            setSelfie(selfieBase64);
          });
        },
      });
    }
  };

  const handleViewBtn = () => {
    var modal = document.getElementById("myModal");
    modal.style.display = "flex";
  }

  const handleCloseBtn = () => {
    var modal = document.getElementById("myModal");
    modal.style.display = "none";
  }

  const handleSubmit = () => {
    // Pass object with metadata: selfieBase64, image_url, response_id
    // If AI image was used, image_url and response_id will be present
    // If non-AI image, only selfieBase64 will be present
    onValueChange({
      selfieBase64: selfie,
      image_url: aiImageUrl || null,
      response_id: aiResponseId || null
    });
    if (download_location) {
      POSTTrackDownload({ download_location })
        .catch((error) => {
          // Silently handle download tracking errors
          // No user-facing action needed for analytics failures
        }
        );
    }
    handleClose();
  };

  const [searchImgCardOpen, setSearchImgCardOpen] = useState(false);

  const handlesearchImgClick = () => {
    setSearchImgCardOpen(!searchImgCardOpen);
  };

  const handlesearchImgChange = (newImg) => {
    // Clear AI metadata when user selects a search image
    setAiImageUrl(null);
    setAiResponseId(null);
    setSelfie(newImg);
  };

  const [download_location, setdownload_location] = useState("");

  const updateDownloadLocation = (downloadurl) => {
    setdownload_location(downloadurl);
  }

  const [aiImageCardOpen, setAiImageCardOpen] = useState(false);

  const handleAiImageCardClick = () => {
    setAiImageCardOpen(!aiImageCardOpen);
  };

  const handleGenerateAIImageClick = () => {
    // Defense-in-depth: Check authentication again before executing
    if (!isAuthenticated) {
      return;
    }

    setAiImageCardOpen(true);
  };

  const waitForTokenRefresh = () => {
    return new Promise((resolve, reject) => {
      if (typeof window === "undefined") {
        reject(new Error("Unable to refresh session. Please try again."));
        return;
      }

      let resolved = false;

      const timeoutId = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          window.removeEventListener("message", handleMessage);
          reject(
            new Error(
              "Session refresh timed out. Please close and reopen this screen."
            )
          );
        }
      }, 15000);

      const handleMessage = (event) => {
        try {
          const data =
            typeof event.data === "string"
              ? JSON.parse(event.data)
              : event.data;

          if (!data || typeof data !== "object") {
            return;
          }

          if (data.type === "token_refreshed" && data.tokenId && !resolved) {
            resolved = true;
            clearTimeout(timeoutId);
            window.removeEventListener("message", handleMessage);
            resolve(data.tokenId);
          } else if (data.type === "token_refresh_failed" && !resolved) {
            resolved = true;
            clearTimeout(timeoutId);
            window.removeEventListener("message", handleMessage);
            reject(
              new Error(
                "Session refresh failed. Please close and reopen this screen."
              )
            );
          }
        } catch {
          // Ignore non-JSON messages
        }
      };

      window.addEventListener("message", handleMessage);
    });
  };

  const handleGenerateAIImage = async (message) => {
    if (!isAuthenticated || !message || !message.trim()) {
      return;
    }

    // First attempt with current token
    const attemptGenerate = async (activeTokenId) => {
      const response = await generateImageAPICall(
        activeTokenId,
        message.trim(),
        messageId
      );

      // Handle API response structure: { status, code, data: { image_url, response_id } }
      if (response.code === "SUCCESS" && response.data?.image_url) {
        // Store AI image metadata
        setAiImageUrl(response.data.image_url);
        setAiResponseId(response.data.response_id || null);
        // Convert image URL to base64 for preview consistency with other image handling
        const imageBase64 = await getBase64FromUrl(response.data.image_url);
        setSelfie(imageBase64);
        setAiImageCardOpen(false);
        return;
      }

      // If backend explicitly signals TOKEN_EXPIRED, delegate to token refresh flow
      if (response.code === "TOKEN_EXPIRED") {
        throw new Error("TOKEN_EXPIRED");
      }

      // Error response - pass error message to card for display
      throw new Error(
        response.message ||
          response.data?.message ||
          "Failed to generate image. Please try again."
      );
    };

    try {
      await attemptGenerate(tokenId);
    } catch (error) {
      // Handle token expiration with WebView bridge when running inside the app
      if (
        error.message === "TOKEN_EXPIRED" &&
        inApp &&
        window?.ReactNativeWebView?.postMessage
      ) {
        // Set up the token refresh listener before sending the refresh request
        const tokenRefreshPromise = waitForTokenRefresh();
        // Request token refresh from the React Native host
        window.ReactNativeWebView.postMessage(
          JSON.stringify({ type: "refresh_token" })
        );

        try {
          const refreshedTokenId = await tokenRefreshPromise;
          setTokenId(refreshedTokenId);
          await attemptGenerate(refreshedTokenId);
          return;
        } catch (refreshError) {
          // Surface a user-friendly, non-technical message
          throw new Error(
            refreshError.message ||
              "Your session has expired. Please close and reopen this screen."
          );
        }
      }

      // Non token-expired errors or non-app environments:
      // Surface a generic, user-friendly message without backend details.
      if (error.message === "TOKEN_EXPIRED") {
        throw new Error(
          "Your session has expired. Please refresh the page and try again."
        );
      }

      throw error;
    }
  };

  return (
    <motion.div
      onClick={(e) => e.stopPropagation()}
      className={styles['edit-card']}
      variants={slideUp}
      initial='hidden'
      animate='visible'
      exit='exit'
      onAnimationStart={(definition) => {
        if (definition === 'visible') {
          window.scrollTo(0, 0);
          document.body.style.overflow = 'hidden';
        }
      }}
    >
      <div className={styles['content-container']}>
        <div className={styles['header']}>
          <CloseIcon
            className={styles['close-button']}
            fontSize='large'
            onClick={handleClose}
          />
          <h1 className={styles['header-text']}>Edit Image</h1>
          {selfie !== initialValue ?
            <button className={styles['save-button']} onClick={handleSubmit}>
              Save
            </button> : <></>}
        </div>
        {selfie !== initialValue ?
          <>
            <img
              id='selfie-preview'
              className={styles['selfie-preview']}
              src={selfie}
              alt='selfie preview'
              onClick={() => selfieInputElement.current.click()}
            />
            <div className={`${styles['button-container']} ${styles['button-container-no-image']}`}>
              <div className={styles['button-wrapper']}>
                <button
                  type="button"
                  onClick={handleGenerateAIImageClick}
                  disabled={!isAuthenticated}
                  aria-disabled={!isAuthenticated}
                  aria-label={isAuthenticated ? "Generate Image with AI" : "Generate Image with AI - Please log in to use this feature"}
                  className={styles['edit-image-button']}
                >
                  <span className={styles['sparkle']}>✨</span> Generate Image with AI <span className={styles['sparkle']}>✨</span>
                </button>
                {!isAuthenticated && (
                  <span className={styles['auth-message']} role="status" aria-live="polite">
                    Please log in to use this feature
                  </span>
                )}
              </div>
              <div className={styles['button-wrapper']}>
                <button
                  type="button"
                  onClick={() => selfieInputElement.current?.click()}
                  className={styles['edit-image-button']}
                >
                  Choose From Gallery/Camera
                </button>
                <span
                  onClick={handleViewBtn}
                  className={height <= 600 ? styles['ask-selfie1'] : styles['ask-selfie']}>
                  Can't take a selfie?
                </span>
              </div>
              {/* Temporarily hide Unsplash search from UI; keep logic for easy restoration */}
              {/* <div className={styles['button-wrapper']}>
                <button
                  type="button"
                  onClick={handlesearchImgClick}
                  className={styles['edit-image-button']}
                >
                  Search Other Images
                </button>
              </div> */}
            </div>
          </>
          : <div className={styles['custom-image-upload-position']}>
            <div className={styles['button-wrapper']}>
              <button
                type="button"
                onClick={handleGenerateAIImageClick}
                disabled={!isAuthenticated}
                aria-disabled={!isAuthenticated}
                aria-label={isAuthenticated ? "Generate Image with AI" : "Generate Image with AI - Please log in to use this feature"}
                className={styles['edit-image-button']}
              >
                <span className={styles['sparkle']}>✨</span> Generate Image with AI <span className={styles['sparkle']}>✨</span>
              </button>
              {!isAuthenticated && (
                <span className={styles['auth-message']} role="status" aria-live="polite">
                  Please log in to use this feature
                </span>
              )}
            </div>
            <div className={styles['button-wrapper']}>
              <button
                type="button"
                onClick={() => selfieInputElement.current?.click()}
                className={styles['edit-image-button']}
              >
                Choose From Gallery/Camera
              </button>
              <span onClick={handleViewBtn} className={styles['ask-selfie']}>
                Can't take a selfie?
              </span>
            </div>
            {/* Temporarily hide Unsplash search from UI; keep logic for easy restoration */}
            {/* <div className={styles['button-wrapper']}>
              <button
                type="button"
                onClick={handlesearchImgClick}
                className={styles['edit-image-button']}
              >
                Search Other Images
              </button>
            </div> */}
          </div>}
        <input
          id='selfie-upload'
          className={styles['default-image-upload']}
          type='file'
          accept="image/*"
          onChange={handleChange}
          ref={selfieInputElement}
        />
      </div>

      <div id="myModal" className={pstyles['edit-card']}>
        <div className={pstyles['content-box']}>
          <div className={pstyles['close-box']}>
            <img
              alt="close button"
              src={close}
              className={pstyles['close']}
              onClick={handleCloseBtn}
            ></img>
          </div>
          <p className={pstyles['text-box']}>If you can't take a selfie/picture please give Gesture access to your camera from your phone's settings. This will allow you to take a picture for the Personalized Message screen.</p>
        </div>
      </div>

      <AnimatePresence>
        {searchImgCardOpen && (
          <SearchImageCard
            handleClose={handlesearchImgClick}
            onValueChange={handlesearchImgChange}
            setDownloadLocation={updateDownloadLocation}
          />
        )}

        {aiImageCardOpen && (
          <AIImageGenerationCard
            handleClose={handleAiImageCardClick}
            initialValue={existingMessageText || ''}
            onGenerate={handleGenerateAIImage}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default SelfieEditCard;
